import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageWithHeadline from '@/components/layout/PageWithHeadline';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { SearchBox, FloatingActionBar, FloatingDeleteButton } from '@/components';
import { useUsers, useInviteUser, useDeleteUsers } from './hooks';
import { UsersTable, InviteUserModal, UserSidePanelHandler } from './components';
import { CompanySidePanelHandler } from '@/features/companies/components';
import { useSidePanelHandler } from '@/hooks/useSidePanelHandler';
import type { UserListItem, UsersQueryParams, UserSidePanelContent } from './types';
import type { CompanySidePanelContent } from '@/features/companies/types';
import type { SortState } from '@/components/tables/SortDirection';
import useCachedValueIfNotNull from '@/utils/CachedValueIf';
import Pagination from '@/components/tables/Pagination';
import useDebounce from '@/utils/useDebounce';
import { useSearch } from '@tanstack/react-router';

interface UsersSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

function setSearchParams(params: UsersSearchParams) {
  const url = new URL(window.location.href);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    } else {
      url.searchParams.delete(key);
    }
  });
  window.history.replaceState({}, '', url.toString());
}

export function UsersPage() {
  const { t } = useTranslation();

  const searchParams = useSearch({ from: '/_app/users' });

  const [searchInput, setSearchInput] = useState(searchParams.search?.toString() || '');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const userSidePanel = useSidePanelHandler<UserSidePanelContent>();
  const companySidePanel = useSidePanelHandler<CompanySidePanelContent>();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const debouncedSearch = useDebounce(searchInput);

  const page = searchParams.page || 1;
  const pageSize = searchParams.pageSize || 10;
  const sortState: SortState = useMemo(
    () => ({
      column: searchParams.sortBy || null,
      direction: searchParams.sortOrder || null,
    }),
    [searchParams.sortBy, searchParams.sortOrder]
  );

  const queryParams: UsersQueryParams = useMemo(
    () => ({
      page,
      pageSize,
      search: debouncedSearch || undefined,
      sortBy: sortState.column || undefined,
      sortOrder: sortState.direction || undefined,
    }),
    [page, pageSize, debouncedSearch, sortState]
  );

  const { data: usersDataResult, isLoading: isLoadingUsers, isFetching } = useUsers(queryParams);
  const inviteUser = useInviteUser();
  const deleteUsers = useDeleteUsers();

  const serverUsers = useCachedValueIfNotNull(usersDataResult, isLoadingUsers, {
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
  });

  const total = usersDataResult?.total || 0;

  const users = useMemo(() => {
    const searchLower = searchInput.toLowerCase();
    return serverUsers.data.filter(
      (user) =>
        user.username.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
    );
  }, [serverUsers, searchInput]);

  const updateSearchParams = useCallback(
    (updates: Partial<UsersSearchParams>) => {
      const newParams: UsersSearchParams = { ...searchParams, ...updates };
      setSearchParams(newParams);
    },
    [searchParams]
  );

  useEffect(() => {
    if (debouncedSearch !== (searchParams.search || '')) {
      updateSearchParams({
        search: debouncedSearch || undefined,
        page: 1,
      });
    }
  }, [debouncedSearch, searchParams.search, updateSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearchParams({ page: newPage });
      setSelectedIds(new Set());
    },
    [updateSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      updateSearchParams({ pageSize: newPageSize, page: 1 });
      setSelectedIds(new Set());
    },
    [updateSearchParams]
  );

  const handleUserClick = useCallback((user: UserListItem) => {
    userSidePanel.open({ type: 'view', userId: user.id });
  }, [userSidePanel]);


  const handleCompanyClick = useCallback((companyId: string, companyName: string) => {
    companySidePanel.open({ type: 'view', company: { uuid: companyId, name: companyName } });
  }, [companySidePanel]);


  const handleInviteUser = useCallback(() => {
    setIsInviteModalOpen(true);
  }, []);

  const handleCloseInviteModal = useCallback(() => {
    setIsInviteModalOpen(false);
  }, []);

  const handleSendInvite = useCallback(
    async (email: string) => {
      const companyId = companySidePanel.content?.type === 'view' ? companySidePanel.content.company.uuid : '';
      await inviteUser.mutateAsync({ email, companyId });
    },
    [inviteUser, companySidePanel]
  );


  return (
    <PageWithHeadline headlineKey="users.title">
      <PageContentCard className="space-y-4 flex-1 min-h-0 flex flex-col w-full">
        <div className="grid md:grid-cols-2 gap-y-2 md:gap-y-0 md:gap-x-5">
          <div>
            <SearchBox
              value={searchInput}
              onChange={setSearchInput}
              placeholder={t('users.search.placeholder')}
              ariaLabel={t('users.search.label')}
              clearAriaLabel={t('users.search.clear')}
            />
          </div>

          {companySidePanel.content?.type === 'view' && (
            <div className="flex flex-wrap md:justify-end items-center gap-2">
              <button
                type="button"
                onClick={handleInviteUser}
                className="py-2 px-3 inline-flex items-center gap-x-1.5 text-sm font-medium rounded-lg bg-primary text-onprimary shadow-2xs hover:bg-primary-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:ring-2 focus:ring-primary"
              >
                <svg
                  className="shrink-0 size-4"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                {t('users.inviteButton')}
              </button>
            </div>)}
        </div>


        <div className="relative grow min-h-0">
          {isFetching && !isLoadingUsers && (
            <div className="absolute top-2 right-2 z-10">
              <div className="animate-spin size-5 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          )}
          <UsersTable
            users={users}
            isLoading={isLoadingUsers}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onUserClick={handleUserClick}
            onCompanyClick={handleCompanyClick}
          />
        </div>

        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </PageContentCard>

      <UserSidePanelHandler
        content={userSidePanel.content}
        panelKey={userSidePanel.panelKey}
        isOpen={userSidePanel.isOpen}
        onClose={userSidePanel.close}
      />

      <CompanySidePanelHandler
        content={companySidePanel.content}
        panelKey={companySidePanel.panelKey}
        isOpen={companySidePanel.isOpen}
        onClose={companySidePanel.close}
      />

      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        onSubmit={handleSendInvite}
        isLoading={inviteUser.isPending}
      />

      <FloatingActionBar selectedCount={selectedIds.size}>
        <FloatingDeleteButton
          onDelete={async () => {
            try {
              await deleteUsers.mutateAsync(Array.from(selectedIds));
              setSelectedIds(new Set());
            } catch (error) {
              console.error('Failed to delete users:', error);
            }
          }}
          isDeleting={deleteUsers.isPending}
          modalTitle={t('users.deleteModal.title')}
          modalMessage={t('users.deleteModal.message')}
        />
      </FloatingActionBar>
    </PageWithHeadline>
  );
}
